import type { ProjectLifecycleStateType } from '../../Layout';
import { isChangeOrderSettled } from '../../Layout';

export type StageSubResolver = (state: ProjectLifecycleStateType) => string | undefined;

export type ProjectStageStepperStageConfig = {
  id: string;
  name: string;
  sub: StageSubResolver;
};

export type ProjectStageStepperConfig = readonly ProjectStageStepperStageConfig[];

/**
 * Defines both which stages the project lifecycle stepper shows and how each
 * one's sub-text is computed. Three stages, matching the workshop table's
 * three simultaneous tracks: Scope, Work, Closeout. Every resolver always
 * returns a definite string: each track has a real, current status at all
 * times, unlike a sequential stepper where an upcoming stage may have
 * nothing yet to say.
 */
export const DEFAULT_PROJECT_STAGE_STEPPER_CONFIG: ProjectStageStepperConfig = [
  {
    id: 'scope',
    name: '',
    sub: state => {
      if (state.phase === 'edit') return 'Scope in draft';
      // Draft/review branches show the prospective next version (one past the
      // last approved scope); once settled, the branch below shows the actual
      // current version instead.
      if (state.phase === 'review') return state.version === 'changeOrder' ? `Change Order ${state.approvedScopeCount} in review` : 'Scope in review';
      if (state.phase === 'approved' && state.version === 'changeOrder' && !isChangeOrderSettled(state)) {
        return `Change Order ${state.approvedScopeCount} in draft`;
      }
      return state.approvedScopeCount > 1 ? `Change Order ${state.approvedScopeCount - 1} is approved` : 'Scope approved';
    },
  },
  {
    id: 'work',
    name: '',
    sub: state => {
      if (state.phase === 'closeout') return 'Work completed';
      return state.workStarted ? 'Work in progress' : 'Work not started';
    },
  },
  {
    id: 'closeout',
    name: '',
    sub: state => {
      if (state.phase !== 'closeout') return 'Closeout unavailable';
      // Checks !projectIsEditable alongside closeoutApproved for the same
      // reason mainNavigationConfig.tsx's approveCloseout does: approving a
      // closeout scan server-side auto-creates a new 'progress' child scan
      // starting back at scanLifecycleStatus 'dispatched', which would make
      // closeoutApproved (this scan's own status) misleadingly false even
      // though the project itself is permanently done.
      return (state.closeoutApproved || !state.projectIsEditable) ? 'Closeout approved' : 'Closeout submitted';
    },
  },
];

/**
 * The terminal, single-stage config shown once isScopeLocked(state) is true -
 * closeout is genuinely, permanently done, so there's nothing left to track
 * across scope/work/closeout, just the one final fact. Rendered as a single
 * "done" stage (ProjectStageStepper.tsx special-cases a one-entry config to
 * never treat it as "active"), matching the design workshop's closed-out
 * terminal state mockup.
 */
export const SCOPE_LOCKED_PROJECT_STAGE_STEPPER_CONFIG: ProjectStageStepperConfig = [
  {
    id: 'closeout',
    name: 'Closeout approved',
    sub: () => 'Project is completed',
  },
];
